import React from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../i18n";
import SEO, { buildBreadcrumb } from "../components/SEO";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  const { locale } = useLocale();
  const en = locale === "en";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: en ? "Why WowRomania Exists" : "De ce există WowRomania",
      url: "https://wowromania.com/about",
      inLanguage: en ? "en" : "ro",
    },
    buildBreadcrumb([
      { name: en ? "Home" : "Acasă", path: "/" },
      { name: en ? "About" : "Despre", path: "/about" },
    ]),
  ];

  return (
    <>
      <SEO
        title={en ? "Why WowRomania Exists" : "De ce există WowRomania"}
        description={en
          ? "The editorial statement behind WowRomania. Why it was built, what makes it different, and the principles that guide every page."
          : "Manifestul editorial al WowRomania. De ce a fost construit, ce îl face diferit și principiile care ghidează fiecare pagină."}
        path="/about"
        type="article"
        jsonLd={jsonLd}
      />
      <article className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="about-page">
        <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
          / {en ? "ABOUT" : "DESPRE"}
        </p>
        <h1 className="font-editorial text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-[var(--wr-ink)] mb-10" data-testid="about-heading">
          {en ? "Why WowRomania exists." : "De ce există WowRomania."}
        </h1>

        <div className="space-y-8 text-[18px] leading-[1.75] text-[var(--wr-ink)] max-w-[720px]">
          {en ? (
            <>
              <p className="font-editorial text-2xl leading-relaxed">
                Romania is one of Europe's most fascinating countries. It deserved a place where planning the journey is as enjoyable as the journey itself.
              </p>
              <p>
                Romania has long inspired travelers, writers, photographers, and historians from around the world. WowRomania was created to add a new chapter to that tradition — combining trusted information, thoughtfully designed planning tools, and an AI travel guide built specifically for Romania. We invite you to explore its regions, traditions, landscapes, and hidden stories with us, one journey at a time.
              </p>
              <p>
                WowRomania was created to bring everything together: trusted information, genuinely useful tools, and an AI travel guide built specifically for Romania.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">Principles</h2>
              <p>
                <strong>Utility first.</strong> Every Tool we build must work with real live data, produce a useful answer in under three seconds, and be honest about what it doesn't know. If a Tool cannot meet that bar, we do not ship it.
              </p>
              <p>
                <strong>Honest recommendations.</strong> When we recommend a stay, a road, a monastery or a restaurant, it is because someone we trust has been there, not because a partner paid for placement. Where affiliate relationships exist, we disclose them per page.
              </p>
              <p>
                <strong>Real planning, not marketing brochures.</strong> Prices, distances, drive times, weather, transit — the boring, load-bearing details of a trip. That is where a great travel product earns its trust.
              </p>
              <p>
                <strong>Respect for Romanian tourism.</strong> Romania is not a backdrop. Not a hidden gem. Not an emerging destination. It is a specific place with specific traditions, and we treat its regions, dialects, dishes and craftspeople with the seriousness they deserve.
              </p>
              <p>
                <strong>Bilingual on purpose.</strong> Romanian and English, treated with equal care. Both available from day one. Both maintained with the same attention to clarity and quality.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">What we are not</h2>
              <p>
                We are not a booking platform. We are not a discount aggregator. We are not a travel-blog network. We are not an AI wrapper that renamed itself for a country.
              </p>
              <p>
                We are an editorial platform with a small number of very good tools and a single, thoughtful AI concierge — built to be the reference place for travelling in Romania.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">This is the beginning</h2>
              <p>
                Phase 1 launches with nine regions, five tools, and one concierge. Phase 2 adds a trip planner, richer per-region content, and affiliates carefully disclosed. Along the way, community contributions, original photography, and partnerships with people who love this country as much as we do.
              </p>
              <p className="font-editorial text-2xl leading-relaxed mt-8">
                If WowRomania makes even one moment of your journey better, then it has fulfilled its purpose.
              </p>
            </>
          ) : (
            <>
              <p className="font-editorial text-2xl leading-relaxed">
                România este una dintre cele mai fascinante țări din Europa. Merita și un loc pe măsură, unde planificarea unei călătorii să fie la fel de frumoasă ca destinația însăși.
              </p>
              <p>
                România i-a inspirat de-a lungul timpului pe călători, scriitori, fotografi și istorici din întreaga lume. WowRomania a fost creat pentru a adăuga un nou capitol acestei tradiții — reunind informații de încredere, instrumente de planificare concepute cu atenție și un ghid de călătorie bazat pe inteligență artificială, dedicat în întregime României. Te invităm să descoperi alături de noi regiunile, tradițiile, peisajele și poveștile mai puțin cunoscute ale acestei țări, o călătorie pe rând.
              </p>
              <p>
                WowRomania s-a născut din dorința de a schimba acest lucru: o platformă care combină informații de încredere, tools utile și un ghid AI construit special pentru România.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">Principii</h2>
              <p>
                <strong>Utilitatea pe primul loc.</strong> Fiecare Tool pe care îl construim trebuie să funcționeze cu date live reale, să producă un răspuns util în mai puțin de trei secunde și să fie onest despre ce nu știe. Dacă un Tool nu îndeplinește ștacheta, nu-l lansăm.
              </p>
              <p>
                <strong>Recomandări oneste.</strong> Când recomandăm o cazare, un drum, o mănăstire sau un restaurant, e pentru că cineva de încredere a fost acolo — nu pentru că un partener a plătit plasarea. Când există relații de afiliere, le anunțăm pe fiecare pagină.
              </p>
              <p>
                <strong>Planificare reală, nu broșuri de marketing.</strong> Prețuri, distanțe, timpi de condus, meteo, transport — detaliile plicticoase și esențiale ale unei călătorii. Acolo își câștigă un produs de călătorie încrederea.
              </p>
              <p>
                <strong>Respect pentru turismul românesc.</strong> România nu e un fundal. Nu e o "bijuterie ascunsă". Nu e o "destinație emergentă". E un loc specific cu tradiții specifice, iar noi tratăm regiunile, dialectele, mâncărurile și meseriașii ei cu seriozitatea pe care o merită.
              </p>
              <p>
                <strong>Bilingv cu intenție.</strong> Română și engleză, tratate cu aceeași grijă. Ambele disponibile încă de la lansare. Ambele întreținute cu aceeași atenție pentru claritate și calitate.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">Ce nu suntem</h2>
              <p>
                Nu suntem o platformă de rezervări. Nu suntem un agregator de reduceri. Nu suntem o rețea de bloguri de călătorii. Nu suntem un wrapper de AI care s-a rebotezat pentru o țară.
              </p>
              <p>
                Suntem o platformă editorială cu un număr mic de tools foarte bune și un singur ghid AI gândit atent — construit ca locul de referință pentru călătoriile în România.
              </p>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight mt-16 mb-4 text-[var(--wr-ink)]">Acesta e începutul</h2>
              <p>
                Faza 1 se lansează cu nouă regiuni, cinci tools și un ghid. Faza 2 adaugă un trip planner, conținut mai bogat pe regiune și afiliați anunțați cu grijă. Pe drum, contribuții din comunitate, fotografie originală și parteneriate cu oameni care iubesc această țară la fel de mult ca noi.
              </p>
              <p className="font-editorial text-2xl leading-relaxed mt-8">
                Dacă WowRomania reușește să facă măcar un moment al călătoriei tale mai frumos, atunci și-a îndeplinit misiunea.
              </p>
            </>
          )}
        </div>

        <div className="mt-20 flex flex-wrap gap-3">
          <Link to="/regions" className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] transition-colors font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="about-cta-regions">
            {en ? "Explore Destinations" : "Explorează destinațiile"} <ArrowUpRight size={14} />
          </Link>
          <Link to="/concierge" className="inline-flex items-center gap-2 px-5 py-3 border border-[var(--wr-ink)] text-[var(--wr-ink)] hover:bg-[var(--wr-ink)] hover:text-[var(--wr-bg)] transition-colors font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="about-cta-concierge">
            {en ? "Ask the Concierge" : "Întreabă Ghidul"} <ArrowUpRight size={14} />
          </Link>
        </div>
      </article>
    </>
  );
}
